import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import { useBaseHistory } from 'spindl-shared-components';

import { filterEmailTemplates, formatDate, getUnderline, filterEmailStatus } from '../../../lib/utils';
import { emailTemplateTooltipContent } from '../../../lib/reactUtils';
import { EmailTemplate, EmailTemplateStatus, EmailTemplateStatusType } from '../../../modules/emailTemplates/types';
import { TemplateCardBlockStateEnum } from '../../blocks/TemplateCardBlock/types';
import { getEmailTemplateStatus } from '../../../modules/emailTemplates/utils';

import { TemplateLibraryPageCombinedProps } from './types';
import { getEmailTemplatesUseCase } from './TemplateLibraryPage.interactor';

const usePresenter = (props: TemplateLibraryPageCombinedProps): TemplateLibraryPageCombinedProps => {
  const { t } = useTranslation();
  const history = useBaseHistory();
  
  const { data: emailTemplates, isLoading } = useQuery('getEmailTemplates', getEmailTemplatesUseCase);
  const [filteredEmailTemplates, setFilteredEmailTemplates] = useState<EmailTemplate[] | undefined>(emailTemplates);
  const [templatesStatus, setTemplatesStatus] = useState<EmailTemplateStatus[]>();
  const [filteredStatus, setFilteredStatus] = useState<EmailTemplateStatus[]>();

  const checkTemplateStatus = useCallback((emailTemplate: EmailTemplate): EmailTemplateStatus => {
    return getEmailTemplateStatus(
      EmailTemplateStatusType.card,
      emailTemplate,
    );
  }, []);

  const handleSubmitQuery = (searchQuery: string) => {
    if (!emailTemplates) {
      return;
    }

    setFilteredEmailTemplates(filterEmailTemplates(searchQuery, emailTemplates));
    setFilteredStatus(filterEmailStatus(searchQuery, emailTemplates, templatesStatus));
  };

  const onItemButtonClicked = (id: number) => {
    history.push({
      pathname: `/editor/${id}`,
      search: '?view=splitView',
    });
  };

  useEffect(() => {
    setFilteredEmailTemplates(emailTemplates);
    setFilteredStatus(templatesStatus);
  }, [emailTemplates, templatesStatus]);

  useEffect(() => {
    setTemplatesStatus(emailTemplates?.map(
      (emailTemplate) => {
        return checkTemplateStatus(emailTemplate);
      }));    
  }, [emailTemplates, checkTemplateStatus]);
  
  let blockState: TemplateCardBlockStateEnum = 'Default';

  if (!emailTemplates?.length) {
    blockState = 'Empty';
  } else {
    if (!filteredEmailTemplates?.length) {
      blockState = 'NoResults';
    }
  }

  if (isLoading) {
    blockState = 'Loading';
  }

  const iconTooltipContent = (errors?: React.ReactNode[]): JSX.Element => { 
    return emailTemplateTooltipContent(errors);
  };

  props = {
    ...props,
    templateCardBlock: {
      ...props.templateCardBlock?.classes,
      className: props.templateCardBlock?.className,
      state: blockState,
      itemCardList: {
        itemCards: filteredEmailTemplates?.map((emailTemplate, index) => {
          const statusMessages = filteredStatus && filteredStatus[index] ? filteredStatus[index].errorMessages : [];
          const errorStatus = statusMessages.length;

          return {
            labelText: {
              value: emailTemplate.name,
            },
            dateText: {
              value: `${t('email_templates.last_modified')} ${formatDate(emailTemplate.updatedAt)}`,
            },
            type: 'Icon',
            error: errorStatus ? 'Error' : 'None',
            iconTooltip: {
              action: 'hover',
              placement: 'bottom',
              targetProps: {
                icon: {
                  asset: errorStatus ? 'Error' : 'EmailFilled',
                  colour: errorStatus ? 'IconDestructive' : 'IconPositiveHover',
                },
              },
              contentProps: {
                align: 'Center',
                content: iconTooltipContent(statusMessages),
              },
            },
            underline: {
              styleType: getUnderline(index),
            },
            onClick: () => onItemButtonClicked(emailTemplate.id),
          };
        }),
      },
      title: {
        value: t(`email_templates.${blockState}.title`),
      },
      instructions: {
        value: t(`email_templates.${blockState}.instructions`),
      },
    },
    templateLibraryHeader: {
      text: {
        value: t('email_templates.page_title'),
      },
      searchBar: {
        textPlaceholder: t('email_templates.search_bar_placeholder'),
        icon: {
          asset: 'Search',
        },
        onSubmit: handleSubmitQuery,
        clearOnBlur: true,
      },
    },
  };

  return props;
};

export default usePresenter;
